import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  agentAnalyticsApi,
  AnalyticsDateRange,
  AnalyticsPeriod,
  AgentMetricsResponse,
  AgentTracesListResponse,
  AgentTraceDetailResponse,
  AgentScoresResponse,
  AgentCostBreakdownResponse,
  AgentKnowledgeGapsResponse,
  AgentHealthScoreResponse,
  AgentHealthTrendsResponse,
  AgentInsightsResponse,
  ResolveGapRequest,
  ResolveGapResponse,
} from '../services/agentAnalyticsApi';

/**
 * Hooks React Query para consumir o módulo agent-analytics.
 *
 * Todos os hooks:
 * - Ficam desabilitados quando `agentId` está vazio (comum em formulário novo)
 * - Stale time de 60s — analytics muda devagar
 * - Não refazem fetch ao focar janela
 * - Query keys: `['agent-analytics', agentId, method, ...params]` — o
 *   intervalo personalizado (`range`) entra por ÚLTIMO, então o prefixo
 *   usado nas invalidações continua o mesmo
 * - `range` (opcional) substitui no backend a janela do `period`
 */

const STALE_TIME = 60 * 1000;

export function useAgentMetrics(
  agentId: string | undefined,
  period: AnalyticsPeriod = '7d',
  range?: AnalyticsDateRange,
) {
  return useQuery<AgentMetricsResponse, Error>({
    queryKey: ['agent-analytics', agentId, 'metrics', period, range ?? null],
    queryFn: () => agentAnalyticsApi.getMetrics(agentId!, period, range),
    enabled: !!agentId,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
  });
}

export function useAgentTraces(
  agentId: string | undefined,
  period: AnalyticsPeriod = '7d',
  options: { limit?: number; page?: number } = {},
  range?: AnalyticsDateRange,
) {
  return useQuery<AgentTracesListResponse, Error>({
    queryKey: [
      'agent-analytics',
      agentId,
      'traces',
      period,
      options.limit,
      options.page,
      range ?? null,
    ],
    queryFn: () =>
      agentAnalyticsApi.listTraces(agentId!, {
        period,
        ...options,
        ...(range ? { range } : {}),
      }),
    enabled: !!agentId,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
  });
}

export function useAgentTraceDetail(
  agentId: string | undefined,
  traceId: string | undefined,
) {
  return useQuery<AgentTraceDetailResponse, Error>({
    queryKey: ['agent-analytics', agentId, 'trace-detail', traceId],
    queryFn: () => agentAnalyticsApi.getTraceDetail(agentId!, traceId!),
    enabled: !!agentId && !!traceId,
    staleTime: 5 * 60 * 1000, // trace detail é imutável, pode cachear mais
    refetchOnWindowFocus: false,
  });
}

export function useAgentScores(
  agentId: string | undefined,
  period: AnalyticsPeriod = '7d',
  range?: AnalyticsDateRange,
) {
  return useQuery<AgentScoresResponse, Error>({
    queryKey: ['agent-analytics', agentId, 'scores', period, range ?? null],
    queryFn: () => agentAnalyticsApi.listScores(agentId!, period, range),
    enabled: !!agentId,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
  });
}

export function useAgentCosts(
  agentId: string | undefined,
  period: AnalyticsPeriod = '30d',
  range?: AnalyticsDateRange,
) {
  return useQuery<AgentCostBreakdownResponse, Error>({
    queryKey: ['agent-analytics', agentId, 'costs', period, range ?? null],
    queryFn: () => agentAnalyticsApi.getCosts(agentId!, period, range),
    enabled: !!agentId,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
  });
}

export function useAgentKnowledgeGaps(
  agentId: string | undefined,
  period: AnalyticsPeriod = '7d',
  includeResolved = false,
  range?: AnalyticsDateRange,
) {
  return useQuery<AgentKnowledgeGapsResponse, Error>({
    queryKey: [
      'agent-analytics',
      agentId,
      'knowledge-gaps',
      period,
      includeResolved,
      range ?? null,
    ],
    queryFn: () =>
      agentAnalyticsApi.getKnowledgeGaps(agentId!, period, includeResolved, range),
    enabled: !!agentId,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
  });
}

/**
 * Mutation pra gerar rascunho de doc-KB pra resolver um gap. Não
 * dispara automaticamente — modal chama com as samples do gap quando
 * abre, exibindo loading enquanto o LLM processa.
 */
export function useGenerateGapDraft(agentId: string | undefined) {
  return useMutation<
    { title: string; content: string },
    Error,
    {
      gapLabel: string;
      samples: Array<{ customerMessage?: string; agentSnippet: string }>;
    }
  >({
    mutationFn: (input) => {
      if (!agentId) throw new Error('agentId é obrigatório');
      return agentAnalyticsApi.generateGapDraft(agentId, input);
    },
  });
}

/**
 * Mutation pra marcar um gap como resolvido. Invalida a query de gaps
 * do agente automaticamente após sucesso.
 */
export function useResolveKnowledgeGap(agentId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<
    ResolveGapResponse,
    Error,
    { gapKey: string; input: ResolveGapRequest }
  >({
    mutationFn: ({ gapKey, input }) => {
      if (!agentId) throw new Error('agentId é obrigatório');
      return agentAnalyticsApi.resolveKnowledgeGap(agentId, gapKey, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agent-analytics', agentId, 'knowledge-gaps'],
      });
    },
  });
}

/**
 * Mutation pra desmarcar um gap resolvido (volta a aparecer na listagem).
 */
export function useUnresolveKnowledgeGap(agentId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { gapKey: string }>({
    mutationFn: ({ gapKey }) => {
      if (!agentId) throw new Error('agentId é obrigatório');
      return agentAnalyticsApi.unresolveKnowledgeGap(agentId, gapKey);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agent-analytics', agentId, 'knowledge-gaps'],
      });
    },
  });
}

export function useAgentHealthScore(
  agentId: string | undefined,
  period: AnalyticsPeriod = '7d',
  range?: AnalyticsDateRange,
) {
  return useQuery<AgentHealthScoreResponse, Error>({
    queryKey: ['agent-analytics', agentId, 'health-score', period, range ?? null],
    queryFn: () => agentAnalyticsApi.getHealthScore(agentId!, period, range),
    enabled: !!agentId,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
  });
}

export function useAgentHealthTrends(
  agentId: string | undefined,
  period: AnalyticsPeriod = '30d',
  range?: AnalyticsDateRange,
) {
  return useQuery<AgentHealthTrendsResponse, Error>({
    queryKey: ['agent-analytics', agentId, 'health-trends', period, range ?? null],
    queryFn: () => agentAnalyticsApi.getHealthTrends(agentId!, period, range),
    enabled: !!agentId,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
  });
}

/**
 * Insights narrativos gerados por LLM — cachear agressivamente no cliente
 * também (5min) já que o backend tem cache de 15min por padrão.
 */
export function useAgentInsights(
  agentId: string | undefined,
  period: AnalyticsPeriod = '7d',
  range?: AnalyticsDateRange,
) {
  return useQuery<AgentInsightsResponse, Error>({
    queryKey: ['agent-analytics', agentId, 'insights', period, range ?? null],
    queryFn: () => agentAnalyticsApi.getInsights(agentId!, period, range),
    enabled: !!agentId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
